import React, { useState, useMemo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Row,
  Col,
  Input,
  Label,
  Form,
  FormGroup
} from 'reactstrap';
import classnames from 'classnames';
import TableContainerReactTable from '../../../../Components/Common/TableContainerReactTable';
import FormInput from '../common/FormInput';
import SelectInput from '../common/SelectInput';

export interface ContactData {
  id: string;
  title: string;
  firstName: string;
  lastName: string;
  middleName: string;
  account: string;
  emailAddress: string;
  homeNumber: string;
  workNumber: string;
  mobileNumber: string;
  active: boolean;
  prospect: boolean;
}

interface ContactFormData {
  title: string;
  firstName: string;
  lastName: string;
  middleName: string;
  preferred: string;
  account: string;
  statusValue: string;
  type: string;
  source: string;
  dateOfBirth: string;
  homeNumber: string;
  workNumber: string;
  mobileNumber: string;
  faxNumber: string;
  emailAddress: string;
  rating: string;
  active: boolean;
  prospect: boolean;
  doNotEmail: boolean;
  doNotMail: boolean;
  doNotCall: boolean;
  doNotSMS: boolean;
  prefCommMethod: string;
  // Billing Address
  billingAddr1: string;
  billingAddr2: string;
  billingAddr3: string;
  billingPostCode: string;
  billingCountry: string;
  billingStateProvince: string;
  // Shipping Address
  shippingAddr1: string;
  shippingAddr2: string;
  shippingAddr3: string;
  shippingPostCode: string;
  shippingCountry: string;
  shippingStateProvince: string;
  copyFromBilling: boolean;
  // Order Defaults
  warehouse: string;
  priceList: string;
  deliveryComments: string;
  extendedDetails: string;
}

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (contact: ContactData) => void;
}

const mockContacts: ContactData[] = [
  {
    id: '1',
    title: 'Mr',
    firstName: 'Admin',
    lastName: 'Customer',
    middleName: '',
    account: 'Dan Murphy\'s',
    emailAddress: 'admin@customer.com',
    homeNumber: '+64 9 123 4567',
    workNumber: '+64 9 123 4568',
    mobileNumber: '+64 21 123 456',
    active: true,
    prospect: false
  },
  {
    id: '2',
    title: 'Ms',
    firstName: 'Friendly',
    lastName: 'Dickson',
    middleName: 'J',
    account: 'Dan Murphy\'s',
    emailAddress: 'friendly@danmurphys.com',
    homeNumber: '+64 4 987 6543',
    workNumber: '+64 4 987 6544',
    mobileNumber: '+64 22 987 654',
    active: true,
    prospect: true
  },
  {
    id: '3',
    title: 'Mr',
    firstName: 'Amber',
    lastName: 'Connor',
    middleName: 'R',
    account: 'RockOn Pty Ltd',
    emailAddress: 'amber@rockon.com.au',
    homeNumber: '+61 3 555 0123',
    workNumber: '+61 3 555 0124',
    mobileNumber: '+61 414 555 012',
    active: true,
    prospect: false
  },
  {
    id: '4',
    title: 'Dr',
    firstName: 'Sarah',
    lastName: 'Johnson',
    middleName: 'M',
    account: 'Tech Solutions Inc',
    emailAddress: 'sarah@techsolutions.co.nz',
    homeNumber: '+64 7 444 5678',
    workNumber: '+64 7 444 5679',
    mobileNumber: '+64 27 444 567',
    active: false,
    prospect: true
  },
  {
    id: '5',
    title: 'Mrs',
    firstName: 'Emma',
    lastName: 'Williams',
    middleName: 'L',
    account: 'Food Distributors Ltd',
    emailAddress: 'emma@fooddist.com',
    homeNumber: '+61 7 333 9876',
    workNumber: '+61 7 333 9877',
    mobileNumber: '+61 423 333 987',
    active: true,
    prospect: false
  }
];

const defaultContactForm: ContactFormData = {
  title: 'Mr',
  firstName: '',
  lastName: '',
  middleName: '',
  preferred: '',
  account: '',
  statusValue: 'Active',
  type: 'Customer',
  source: '',
  dateOfBirth: '',
  homeNumber: '',
  workNumber: '',
  mobileNumber: '',
  faxNumber: '',
  emailAddress: '',
  rating: '',
  active: true,
  prospect: false,
  doNotEmail: false,
  doNotMail: false,
  doNotCall: false,
  doNotSMS: false,
  prefCommMethod: 'Email',
  billingAddr1: '',
  billingAddr2: '',
  billingAddr3: '',
  billingPostCode: '',
  billingCountry: 'Australia',
  billingStateProvince: '',
  shippingAddr1: '',
  shippingAddr2: '',
  shippingAddr3: '',
  shippingPostCode: '',
  shippingCountry: 'Australia',
  shippingStateProvince: '',
  copyFromBilling: false,
  warehouse: 'Main Warehouse',
  priceList: 'Default Price List',
  deliveryComments: '',
  extendedDetails: ''
};

const searchFieldOptions = ['First Name', 'Last Name', 'Account', 'Email Address'];
const titleOptions = ['Mr', 'Mrs', 'Ms', 'Dr', 'Prof'];
const statusOptions = ['Active', 'Inactive', 'Pending', 'Suspended'];
const typeOptions = ['Customer', 'Supplier', 'Both', 'Prospect'];
const sourceOptions = ['Website', 'Referral', 'Advertisement', 'Cold Call', 'Trade Show', 'Other'];
const ratingOptions = ['A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'D', 'F'];
const prefCommMethodOptions = ['Email', 'Phone', 'Mail', 'SMS', 'None'];
const countryOptions = ['Australia', 'New Zealand', 'United States', 'United Kingdom', 'Canada'];
const stateProvinceOptions = [
  'Australian Capital Territory', 'New South Wales', 'Northern Territory', 'Queensland',
  'South Australia', 'Tasmania', 'Victoria', 'Western Australia',
  'Auckland', 'Wellington', 'Canterbury', 'Otago'
];
const warehouseOptions = ['Main Warehouse', 'Secondary Warehouse', 'Distribution Center'];
const priceListOptions = ['Default Price List', 'Premium Price List', 'Wholesale Price List'];

const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose, onSelect }) => {
  const [activeTab, setActiveTab] = useState('1');
  const [searchField, setSearchField] = useState('First Name');
  const [searchValue, setSearchValue] = useState('');
  const [contacts, setContacts] = useState<ContactData[]>(mockContacts);
  const [selectedContact, setSelectedContact] = useState<ContactData | null>(null);
  const [contactForm, setContactForm] = useState<ContactFormData>(defaultContactForm);

  const columns = useMemo(() => [
    {
      header: 'Title',
      accessorKey: 'title',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => handleRowClick(cell.row.original)}
          className={selectedContact?.id === cell.row.original.id ? 'fw-bold text-primary' : ''}
        >
          {cell.getValue()}
        </span>
      )
    },
    {
      header: 'Last Name',
      accessorKey: 'lastName',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => handleRowClick(cell.row.original)}
          className={selectedContact?.id === cell.row.original.id ? 'fw-bold text-primary' : ''}
        >
          {cell.getValue()}
        </span>
      )
    },
    {
      header: 'First Name',
      accessorKey: 'firstName',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => handleRowClick(cell.row.original)}
          className={selectedContact?.id === cell.row.original.id ? 'fw-bold text-primary' : ''}
        >
          {cell.getValue()}
        </span>
      )
    },
    {
      header: 'Middle Name',
      accessorKey: 'middleName',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => handleRowClick(cell.row.original)}
          className={selectedContact?.id === cell.row.original.id ? 'fw-bold text-primary' : ''}
        >
          {cell.getValue()}
        </span>
      )
    },
    {
      header: 'Account',
      accessorKey: 'account',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => handleRowClick(cell.row.original)}
          className={selectedContact?.id === cell.row.original.id ? 'fw-bold text-primary' : ''}
        >
          {cell.getValue()}
        </span>
      )
    },
    {
      header: 'Prospect',
      accessorKey: 'prospect',
      enableColumnFilter: false,
      cell: (cell: any) => cell.getValue() ? (
        <div className="text-center"><i className="ri-checkbox-circle-line fs-17 align-middle text-success"></i></div>
      ) : null
    },
    {
      header: 'Email Address',
      accessorKey: 'emailAddress',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => handleRowClick(cell.row.original)}
          className={selectedContact?.id === cell.row.original.id ? 'fw-bold text-primary' : ''}
        >
          {cell.getValue()}
        </span>
      )
    }
  ], [selectedContact]);

  const handleSearch = () => {
    const filtered = mockContacts.filter(contact => {
      let fieldValue = '';
      switch (searchField) {
        case 'First Name':
          fieldValue = contact.firstName;
          break;
        case 'Last Name':
          fieldValue = contact.lastName;
          break;
        case 'Account':
          fieldValue = contact.account;
          break;
        case 'Email Address':
          fieldValue = contact.emailAddress;
          break;
        default:
          fieldValue = contact.firstName;
      }
      return fieldValue.toLowerCase().includes(searchValue.toLowerCase());
    });
    setContacts(filtered);
  };

  const handleClear = () => {
    setSearchValue('');
    setContacts(mockContacts);
    setSelectedContact(null);
  };

  const handleRowClick = (contact: ContactData) => {
    setSelectedContact(contact);
    // Populate view form with selected data
    setContactForm({
      ...contactForm,
      title: contact.title,
      firstName: contact.firstName,
      lastName: contact.lastName,
      middleName: contact.middleName,
      account: contact.account,
      emailAddress: contact.emailAddress,
      homeNumber: contact.homeNumber,
      workNumber: contact.workNumber,
      mobileNumber: contact.mobileNumber,
      active: contact.active,
      prospect: contact.prospect
    });
  };

  const handleCancel = () => {
    setSelectedContact(null);
    onClose();
  };

  const handleSelect = () => {
    if (selectedContact && onSelect) {
      onSelect(selectedContact);
    }
    onClose();
  };

  const handleContactFormChange = (field: keyof ContactFormData, value: any) => {
    setContactForm(prev => ({ ...prev, [field]: value }));
  };

  const handleContactFormCheckbox = (field: keyof ContactFormData) => {
    setContactForm(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleCopyFromBilling = () => {
    if (contactForm.copyFromBilling) {
      setContactForm(prev => ({
        ...prev,
        shippingAddr1: prev.billingAddr1,
        shippingAddr2: prev.billingAddr2,
        shippingAddr3: prev.billingAddr3,
        shippingPostCode: prev.billingPostCode,
        shippingCountry: prev.billingCountry,
        shippingStateProvince: prev.billingStateProvince
      }));
    }
  };

  const handleNewContactClear = () => setContactForm(defaultContactForm);

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="xl">
      <ModalHeader toggle={onClose}>Contact</ModalHeader>
      <ModalBody>
        <Nav pills className="nav nav-tabs-custom arrow-navtabs nav-success mb-3">
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '1' })}
              onClick={() => setActiveTab('1')}
              style={{ cursor: 'pointer' }}
            >
              Search
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '2' })}
              onClick={() => setActiveTab('2')}
              style={{ cursor: 'pointer' }}
            >
              Adv Search
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '3' })}
              onClick={() => setActiveTab('3')}
              style={{ cursor: 'pointer' }}
            >
              View
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '4' })}
              onClick={() => setActiveTab('4')}
              style={{ cursor: 'pointer' }}
            >
              New
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          {/* Search Tab */}
          <TabPane tabId="1">
            <Row className="mb-1">
              <Col md={2}>
                <FormInput
                  id="firstName"
                  label="First Name"
                  value={searchValue}
                  onChange={setSearchValue}
                />
              </Col>
              <Col md={2}>
                <FormInput
                  id="lastName"
                  label="Last Name"
                  value=""
                  onChange={() => {}}
                />
              </Col>
              <Col md={2}>
                <FormInput
                  id="account"
                  label="Account"
                  value=""
                  onChange={() => {}}
                  hasSearchButton
                />
              </Col>
              <Col md={2}>
                <FormInput
                  id="emailAddress"
                  label="Email Address"
                  value=""
                  onChange={() => {}}
                />
              </Col>
              <Col md={2}>
                <SelectInput
                  formData={{ prospect: '' }}
                  setFormData={() => {}}
                  label="Prospect"
                  id="prospect"
                  options={['', 'Yes', 'No']}
                />
              </Col>
              <Col md={2}>
                <div className="d-flex gap-2 mt-3">
                  <button className="btn btn-soft-primary btn-sm" style={{ minWidth: 80 }} onClick={handleSearch}>Search</button>
                  <button className="btn btn-soft-warning btn-sm" style={{ minWidth: 80 }} onClick={handleClear}>Clear</button>
                </div>
              </Col>
            </Row>

            <TableContainerReactTable
              columns={columns}
              data={contacts}
              divClass="table-responsive"
              tableClass="align-middle table-nowrap"
              theadClass="table-light"
            />
          </TabPane>

          {/* Advanced Search Tab */}
          <TabPane tabId="2">
            <Row className="justify-content-center align-items-center">
              <Col md={12} className="text-center">
                <p className="text-muted">Advanced Search functionality coming soon...</p>
              </Col>
            </Row>
          </TabPane>

          {/* View Tab */}
          <TabPane tabId="3">
            <Form>
              <Row className="mb-2">
                <Col md={2}>
                  <SelectInput
                    formData={contactForm}
                    setFormData={setContactForm}
                    label="Title"
                    id="title"
                    options={titleOptions}
                  />
                </Col>
                <Col md={2}>
                  <FormInput
                    id="viewFirstName"
                    label="First Name *"
                    value={contactForm.firstName}
                    onChange={(val) => handleContactFormChange('firstName', val)}
                    required
                  />
                </Col>
                <Col md={2}>
                  <FormInput
                    id="viewLastName"
                    label="Last Name *"
                    value={contactForm.lastName}
                    onChange={(val) => handleContactFormChange('lastName', val)}
                    required
                  />
                </Col>
                <Col md={2}>
                  <FormInput
                    id="viewMiddleName"
                    label="Middle"
                    value={contactForm.middleName}
                    onChange={(val) => handleContactFormChange('middleName', val)}
                  />
                </Col>
                <Col md={2}>
                  <FormInput
                    id="viewPreferred"
                    label="Preferred"
                    value={contactForm.preferred}
                    onChange={(val) => handleContactFormChange('preferred', val)}
                  />
                </Col>
                <Col md={2}>
                  <FormInput
                    id="viewAccount"
                    label="Account"
                    value={contactForm.account}
                    onChange={(val) => handleContactFormChange('account', val)}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col md={2}>
                  <SelectInput
                    formData={contactForm}
                    setFormData={setContactForm}
                    label="Status"
                    id="statusValue"
                    options={statusOptions}
                  />
                </Col>
                <Col md={2}>
                  <SelectInput
                    formData={contactForm}
                    setFormData={setContactForm}
                    label="Type"
                    id="type"
                    options={typeOptions}
                  />
                </Col>
                <Col md={2}>
                  <SelectInput
                    formData={contactForm}
                    setFormData={setContactForm}
                    label="Source"
                    id="source"
                    options={sourceOptions}
                  />
                </Col>
                <Col md={2}>
                  <FormInput
                    id="viewDateOfBirth"
                    label="Date of Birth"
                    value={contactForm.dateOfBirth}
                    onChange={(val) => handleContactFormChange('dateOfBirth', val)}
                    type="date"
                  />
                </Col>
                <Col md={2}>
                  <FormInput
                    id="viewEmailAddress"
                    label="Email Address"
                    value={contactForm.emailAddress}
                    onChange={(val) => handleContactFormChange('emailAddress', val)}
                  />
                </Col>
                <Col md={2}>
                  <SelectInput
                    formData={contactForm}
                    setFormData={setContactForm}
                    label="Rating"
                    id="rating"
                    options={ratingOptions}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col md={3}>
                  <FormInput
                    id="viewHomeNumber"
                    label="Home Number"
                    value={contactForm.homeNumber}
                    onChange={(val) => handleContactFormChange('homeNumber', val)}
                    hasSearchButton
                  />
                </Col>
                <Col md={3}>
                  <FormInput
                    id="viewWorkNumber"
                    label="Work Number"
                    value={contactForm.workNumber}
                    onChange={(val) => handleContactFormChange('workNumber', val)}
                    hasSearchButton
                  />
                </Col>
                <Col md={3}>
                  <FormInput
                    id="viewMobileNumber"
                    label="Mobile Number"
                    value={contactForm.mobileNumber}
                    onChange={(val) => handleContactFormChange('mobileNumber', val)}
                  />
                </Col>
                <Col md={3}>
                  <FormInput
                    id="viewFaxNumber"
                    label="Fax Number"
                    value={contactForm.faxNumber}
                    onChange={(val) => handleContactFormChange('faxNumber', val)}
                    hasSearchButton
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col md={6}>
                  <div className="mb-1">
                    <Label className="form-label mb-0">Extended Details</Label>
                    <textarea
                      className="form-control form-control-sm"
                      rows={2}
                      value={contactForm.extendedDetails}
                      onChange={(e) => handleContactFormChange('extendedDetails', e.target.value)}
                    />
                  </div>
                </Col>
                <Col md={1}>
                  <div className="form-check mt-4">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="newActive"
                      checked={contactForm.active}
                      onChange={() => handleContactFormCheckbox('active')}
                    />
                    <Label className="form-check-label" htmlFor="newActive">
                      Active
                    </Label>
                  </div>
                </Col>
                <Col md={1}>
                  <div className="form-check mt-4">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="newProspect"
                      checked={contactForm.prospect}
                      onChange={() => handleContactFormCheckbox('prospect')}
                    />
                    <Label className="form-check-label" htmlFor="newProspect">
                      Prospect
                    </Label>
                  </div>
                </Col><Col md={1}>
                  <div className="form-check mt-4">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="newDoNotEmail"
                      checked={contactForm.doNotEmail}
                      onChange={() => handleContactFormCheckbox('doNotEmail')}
                    />
                    <Label className="form-check-label" htmlFor="newDoNotEmail">
                      Do Not Email
                    </Label>
                  </div>
                </Col><Col md={1}>
                  <div className="form-check mt-4">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="newDoNotMail"
                      checked={contactForm.doNotMail}
                      onChange={() => handleContactFormCheckbox('doNotMail')}
                    />
                    <Label className="form-check-label" htmlFor="newDoNotMail">
                      Do Not Mail
                    </Label>
                  </div>
                </Col><Col md={1}>
                  <div className="form-check mt-4">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="newDoNotCall"
                      checked={contactForm.doNotCall}
                      onChange={() => handleContactFormCheckbox('doNotCall')}
                    />
                    <Label className="form-check-label" htmlFor="newActive">
                      Do Not Call
                    </Label>
                  </div>
                </Col><Col md={1}>
                  <div className="form-check mt-4">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="newDoNotSMS"
                      checked={contactForm.doNotSMS}
                      onChange={() => handleContactFormCheckbox('doNotSMS')}
                    />
                    <Label className="form-check-label" htmlFor="newDoNotSMS">
                      Do Not SMS
                    </Label>
                  </div>
                </Col>
              </Row>
            </Form>
          </TabPane>

          {/* New Tab */}
          <TabPane tabId="4">
            <Form>
              {/* Main Contact Information */}
              <Row className="mb-2">
                <Col md={2}>
                  <SelectInput
                    formData={contactForm}
                    setFormData={setContactForm}
                    label="Title"
                    id="title"
                    options={titleOptions}
                  />
                </Col>
                <Col md={2}>
                  <FormInput
                    id="newFirstName"
                    label="First Name *"
                    value={contactForm.firstName}
                    onChange={(val) => handleContactFormChange('firstName', val)}
                    required
                  />
                </Col>
                <Col md={2}>
                  <FormInput
                    id="newLastName"
                    label="Last Name *"
                    value={contactForm.lastName}
                    onChange={(val) => handleContactFormChange('lastName', val)}
                    required
                  />
                </Col>
                <Col md={2}>
                  <FormInput
                    id="newMiddleName"
                    label="Middle"
                    value={contactForm.middleName}
                    onChange={(val) => handleContactFormChange('middleName', val)}
                  />
                </Col>
                <Col md={2}>
                  <FormInput
                    id="newPreferred"
                    label="Preferred"
                    value={contactForm.preferred}
                    onChange={(val) => handleContactFormChange('preferred', val)}
                  />
                </Col>
                <Col md={2}>
                  <FormInput
                    id="newAccount"
                    label="Account"
                    value={contactForm.account}
                    onChange={(val) => handleContactFormChange('account', val)}
                    hasSearchButton
                  />
                </Col>
              </Row>

              <Row className="mb-2">
                <Col md={2}>
                  <SelectInput
                    formData={contactForm}
                    setFormData={setContactForm}
                    label="Status"
                    id="statusValue"
                    options={statusOptions}
                  />
                </Col>
                <Col md={2}>
                  <SelectInput
                    formData={contactForm}
                    setFormData={setContactForm}
                    label="Type"
                    id="type"
                    options={typeOptions}
                  />
                </Col>
                <Col md={2}>
                  <SelectInput
                    formData={contactForm}
                    setFormData={setContactForm}
                    label="Source"
                    id="source"
                    options={sourceOptions}
                  />
                </Col>
                <Col md={2}>
                  <FormInput
                    id="newDateOfBirth"
                    label="Date of Birth"
                    value={contactForm.dateOfBirth}
                    onChange={(val) => handleContactFormChange('dateOfBirth', val)}
                    type="date"
                  />
                </Col>
                <Col md={2}>
                  <FormInput
                    id="viewEmailAddress"
                    label="Email Address"
                    value={contactForm.emailAddress}
                    onChange={(val) => handleContactFormChange('emailAddress', val)}
                  />
                </Col>
                <Col md={2}>
                  <SelectInput
                    formData={contactForm}
                    setFormData={setContactForm}
                    label="Rating"
                    id="rating"
                    options={ratingOptions}
                  />
                </Col>
              </Row>

              {/* Contact Numbers */}
              <Row className="mb-2">
                <Col md={3}>
                  <FormInput
                    id="newHomeNumber"
                    label="Home Number"
                    value={contactForm.homeNumber}
                    onChange={(val) => handleContactFormChange('homeNumber', val)}
                    hasSearchButton
                  />
                </Col>
                <Col md={3}>
                  <FormInput
                    id="newWorkNumber"
                    label="Work Number"
                    value={contactForm.workNumber}
                    onChange={(val) => handleContactFormChange('workNumber', val)}
                    hasSearchButton
                  />
                </Col>
                <Col md={3}>
                  <FormInput
                    id="newMobileNumber"
                    label="Mobile Number"
                    value={contactForm.mobileNumber}
                    onChange={(val) => handleContactFormChange('mobileNumber', val)}
                  />
                </Col>
                <Col md={3}>
                  <FormInput
                    id="newFaxNumber"
                    label="Fax Number"
                    value={contactForm.faxNumber}
                    onChange={(val) => handleContactFormChange('faxNumber', val)}
                    hasSearchButton
                  />
                </Col>
              </Row>

              {/* Email and Communication */}
              <Row className="mb-2">
              <Col md={3}>
                  <SelectInput
                    formData={contactForm}
                    setFormData={setContactForm}
                    label="Pref Comm Method"
                    id="prefCommMethod"
                    options={prefCommMethodOptions}
                  />
                </Col>
              <Col md={1}>
                  <div className="form-check mt-4">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="newActive"
                      checked={contactForm.active}
                      onChange={() => handleContactFormCheckbox('active')}
                    />
                    <Label className="form-check-label" htmlFor="newActive">
                      Active
                    </Label>
                  </div>
                </Col>
                <Col md={1}>
                  <div className="form-check mt-4">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="newProspect"
                      checked={contactForm.prospect}
                      onChange={() => handleContactFormCheckbox('prospect')}
                    />
                    <Label className="form-check-label" htmlFor="newProspect">
                      Prospect
                    </Label>
                  </div>
                </Col><Col md={1}>
                  <div className="form-check mt-4">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="newDoNotEmail"
                      checked={contactForm.doNotEmail}
                      onChange={() => handleContactFormCheckbox('doNotEmail')}
                    />
                    <Label className="form-check-label" htmlFor="newDoNotEmail">
                      Do Not Email
                    </Label>
                  </div>
                </Col><Col md={1}>
                  <div className="form-check mt-4">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="newDoNotMail"
                      checked={contactForm.doNotMail}
                      onChange={() => handleContactFormCheckbox('doNotMail')}
                    />
                    <Label className="form-check-label" htmlFor="newDoNotMail">
                      Do Not Mail
                    </Label>
                  </div>
                </Col><Col md={1}>
                  <div className="form-check mt-4">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="newDoNotCall"
                      checked={contactForm.doNotCall}
                      onChange={() => handleContactFormCheckbox('doNotCall')}
                    />
                    <Label className="form-check-label" htmlFor="newActive">
                      Do Not Call
                    </Label>
                  </div>
                </Col><Col md={1}>
                  <div className="form-check mt-4">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="newDoNotSMS"
                      checked={contactForm.doNotSMS}
                      onChange={() => handleContactFormCheckbox('doNotSMS')}
                    />
                    <Label className="form-check-label" htmlFor="newDoNotSMS">
                      Do Not SMS
                    </Label>
                  </div>
                </Col>
              </Row>

              {/* Address Information */}
              <Row className="mb-3">
                <Col md={6}>
                  <fieldset className="border p-2">
                    <legend className="float-none w-auto px-2 mb-0 text-body" style={{ fontSize: '0.8rem' }}>Billing Address</legend>
                    <Row>
                      <Col md={6}>
                        <FormInput
                          id="billingAddr1"
                          label="Addr 1"
                          value={contactForm.billingAddr1}
                          onChange={(val) => handleContactFormChange('billingAddr1', val)}
                        />
                      </Col>
                      <Col md={6}>
                        <FormInput
                          id="billingAddr2"
                          label="Addr 2"
                          value={contactForm.billingAddr2}
                          onChange={(val) => handleContactFormChange('billingAddr2', val)}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <FormInput
                          id="billingAddr3"
                          label="Addr 3"
                          value={contactForm.billingAddr3}
                          onChange={(val) => handleContactFormChange('billingAddr3', val)}
                        />
                      </Col>
                      <Col md={6}>
                        <FormInput
                          id="billingPostCode"
                          label="Post Code"
                          value={contactForm.billingPostCode}
                          onChange={(val) => handleContactFormChange('billingPostCode', val)}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <SelectInput
                          formData={contactForm}
                          setFormData={setContactForm}
                          label="Country"
                          id="billingCountry"
                          options={countryOptions}
                        />
                      </Col>
                      <Col md={6}>
                        <SelectInput
                          formData={contactForm}
                          setFormData={setContactForm}
                          label="State/Province"
                          id="billingStateProvince"
                          options={stateProvinceOptions}
                        />
                      </Col>
                    </Row>
                  </fieldset>
                </Col>
                <Col md={6}>
                  <fieldset className="border p-2">
                    <legend className="float-none w-auto px-2 mb-0 text-body" style={{ fontSize: '0.8rem' }}>Shipping Address</legend>
                    <div className="form-check mb-2">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="copyFromBilling"
                        checked={contactForm.copyFromBilling}
                        onChange={() => {
                          handleContactFormCheckbox('copyFromBilling');
                          handleCopyFromBilling();
                        }}
                      />
                      <Label className="form-check-label" htmlFor="copyFromBilling">
                        Copy from Billing
                      </Label>
                    </div>
                    <Row>
                      <Col md={6}>
                        <FormInput
                          id="shippingAddr1"
                          label="Addr 1"
                          value={contactForm.shippingAddr1}
                          onChange={(val) => handleContactFormChange('shippingAddr1', val)}
                        />
                      </Col>
                      <Col md={6}>
                        <FormInput
                          id="shippingAddr2"
                          label="Addr 2"
                          value={contactForm.shippingAddr2}
                          onChange={(val) => handleContactFormChange('shippingAddr2', val)}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <FormInput
                          id="shippingAddr3"
                          label="Addr 3"
                          value={contactForm.shippingAddr3}
                          onChange={(val) => handleContactFormChange('shippingAddr3', val)}
                        />
                      </Col>
                      <Col md={6}>
                        <FormInput
                          id="shippingPostCode"
                          label="Post Code"
                          value={contactForm.shippingPostCode}
                          onChange={(val) => handleContactFormChange('shippingPostCode', val)}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <SelectInput
                          formData={contactForm}
                          setFormData={setContactForm}
                          label="Country"
                          id="shippingCountry"
                          options={countryOptions}
                        />
                      </Col>
                      <Col md={6}>
                        <SelectInput
                          formData={contactForm}
                          setFormData={setContactForm}
                          label="State/Province"
                          id="shippingStateProvince"
                          options={stateProvinceOptions}
                        />
                      </Col>
                    </Row>
                  </fieldset>
                </Col>
              </Row>

              {/* Order Defaults and Comments */}
              <Row className="mb-2">
                <Col md={6}>
                  <fieldset className="border p-2">
                    <legend className="float-none w-auto px-2 mb-0 text-body" style={{ fontSize: '0.8rem' }}>Order Defaults</legend>
                    <Row>
                      <Col md={6}>
                        <SelectInput
                          formData={contactForm}
                          setFormData={setContactForm}
                          label="Warehouse"
                          id="warehouse"
                          options={warehouseOptions}
                        />
                      </Col>
                      <Col md={6}>
                        <SelectInput
                          formData={contactForm}
                          setFormData={setContactForm}
                          label="Price List"
                          id="priceList"
                          options={priceListOptions}
                        />
                      </Col>
                    </Row>
                  </fieldset>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label className="form-label mb-0">Delivery Comments</Label>
                    <textarea
                      className="form-control form-control-sm"
                      rows={4}
                      value={contactForm.deliveryComments}
                      onChange={(e) => handleContactFormChange('deliveryComments', e.target.value)}
                    />
                  </div>
                </Col>
              </Row>
            </Form>
          </TabPane>
        </TabContent>
      </ModalBody>

      <ModalFooter className="d-flex justify-content-between">
        <div></div>
        <div className="d-flex gap-2 justify-content-end">
          {activeTab === '4' ? (
            // New tab buttons
            <>
              <button className="btn btn-soft-warning btn-sm" style={{ minWidth: 80 }} onClick={handleNewContactClear}>Clear</button>
              <button className="btn btn-soft-primary btn-sm" style={{ minWidth: 80 }}>Check</button>
              <button className="btn btn-soft-success btn-sm" style={{ minWidth: 80 }}>Create</button>
            </>
          ) : activeTab === '3' ? (
            // View tab buttons
            <>
              <button className="btn btn-soft-primary btn-sm" style={{ minWidth: 80 }}>Prev</button>
              <button className="btn btn-soft-primary btn-sm" style={{ minWidth: 80 }}>Next</button>
              <button className="btn btn-soft-success btn-sm" style={{ minWidth: 80 }} onClick={handleSelect}>Pick</button>
            </>
          ) : activeTab === '1' ? (
            // Search tab buttons
            <>
              <button className="btn btn-soft-warning btn-sm" style={{ minWidth: 80 }} onClick={handleClear}>Clear</button>
            </>
          ) : null}
          <button className="btn btn-soft-dark btn-sm" style={{ minWidth: 80 }} onClick={handleCancel}>Cancel</button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ContactModal; 